import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { 
  Video, Plus, Play, Pause, StopCircle, AlertTriangle, CheckCircle, Eye, Bell, 
  TrendingUp, Activity, Camera, Wifi, WifiOff, Shield, Zap, Radio, Monitor,
  Sparkles, Target, Clock, AlertCircle, Settings, RefreshCw
} from 'lucide-react';
import { Progress } from './ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Professional header image - surveillance/monitoring
const HEADER_IMAGE_URL = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1600&q=80";

// Feature highlights for the monitoring dashboard
const MONITORING_FEATURES = [
  { icon: Video, label: "Live Feed", description: "Real-time camera streams", color: "text-blue-500", bg: "bg-blue-50", gradient: "from-blue-500 to-indigo-500" },
  { icon: Shield, label: "AI Detection", description: "Automated hazard alerts", color: "text-green-500", bg: "bg-green-50", gradient: "from-green-500 to-emerald-500" },
  { icon: Bell, label: "Smart Alerts", description: "Instant notifications", color: "text-amber-500", bg: "bg-amber-50", gradient: "from-amber-500 to-orange-500" },
  { icon: TrendingUp, label: "Analytics", description: "Performance insights", color: "text-purple-500", bg: "bg-purple-50", gradient: "from-purple-500 to-pink-500" }
];

const LiveMonitoringPage = () => {
  const [cameras, setCameras] = useState([]);
  const [monitors, setMonitors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCamera, setShowAddCamera] = useState(false);
  const [showStartMonitor, setShowStartMonitor] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  const [cameraForm, setCameraForm] = useState({
    camera_name: '',
    company_id: '',
    location: '',
    stream_url: '',
    active: true
  });

  const [monitorForm, setMonitorForm] = useState({
    camera_id: '',
    company_id: '',
    capture_interval_seconds: 30,
    alert_threshold: 0.7
  });

  useEffect(() => {
    fetchData();
    // Set up polling for real-time updates
    const interval = setInterval(fetchData, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [selectedCompany]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch companies
      const companiesResponse = await axios.get(`${API}/companies`);
      setCompanies(companiesResponse.data || []);
      
      if (companiesResponse.data && companiesResponse.data.length > 0) {
        const companyId = selectedCompany || companiesResponse.data[0].id;
        setSelectedCompany(companyId);
        
        // Fetch cameras
        const camerasResponse = await axios.get(`${API}/cctv/cameras?company_id=${companyId}`);
        setCameras(camerasResponse.data || []);
        
        // Fetch active monitors
        const monitorsResponse = await axios.get(`${API}/monitoring/active?company_id=${companyId}`);
        setMonitors(monitorsResponse.data || []);
        
        // Fetch alerts
        const alertsResponse = await axios.get(`${API}/monitoring/alerts?company_id=${companyId}&acknowledged=false`);
        setAlerts(alertsResponse.data || []);
        
        // Fetch stats
        const statsResponse = await axios.get(`${API}/monitoring/stats/${companyId}`);
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCamera = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cctv/cameras`, {
        ...cameraForm,
        company_id: selectedCompany
      });
      toast({
        title: "Success",
        description: "Camera added successfully"
      });
      setShowAddCamera(false);
      setCameraForm({
        camera_name: '',
        company_id: '',
        location: '',
        stream_url: '',
        active: true
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add camera",
        variant: "destructive"
      });
    }
  };

  const handleStartMonitoring = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/monitoring/start`, {
        ...monitorForm,
        company_id: selectedCompany
      });
      toast({
        title: "Success",
        description: "Live monitoring started successfully"
      });
      setShowStartMonitor(false);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to start monitoring",
        variant: "destructive"
      });
    }
  };

  const handleUpdateMonitorStatus = async (monitorId, status) => {
    try {
      await axios.put(`${API}/monitoring/${monitorId}/status?status=${status}`);
      toast({
        title: "Success",
        description: `Monitor ${status} successfully`
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update monitor status",
        variant: "destructive"
      });
    }
  };

  const handleManualCapture = async (monitorId) => {
    try {
      const response = await axios.post(`${API}/monitoring/${monitorId}/capture`);
      toast({
        title: "Capture Complete",
        description: `Analyzed frame. ${response.data.violations_detected} violations detected.`,
        variant: response.data.alert_created ? "destructive" : "default"
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to capture and analyze",
        variant: "destructive"
      });
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API}/monitoring/alerts/${alertId}/acknowledge`);
      toast({
        title: "Success",
        description: "Alert acknowledged"
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to acknowledge alert",
        variant: "destructive"
      });
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      stopped: 'bg-gray-100 text-gray-800',
      error: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stylized Header with Visual */}
      <div className="relative rounded-2xl overflow-hidden shadow-xl">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HEADER_IMAGE_URL})` }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-blue-900/90 to-cyan-900/85" />
        
        {/* Content */}
        <div className="relative px-8 py-10">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <Monitor className="w-7 h-7 text-white" />
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500/20 text-green-300 border-green-500/30 px-3 py-1">
                    <Radio className="w-3 h-3 mr-1 animate-pulse" />
                    Live
                  </Badge>
                  <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 px-3 py-1">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI-Powered
                  </Badge>
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Live Safety Monitoring
              </h1>
              <p className="text-lg text-blue-100/90 max-w-2xl">
                Real-time CCTV and drone AI analysis for proactive workplace safety management with automated hazard detection
              </p>
            </div>
            
            {/* Quick Stats in Header */}
            {stats && (
              <div className="hidden lg:flex gap-3">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                  <Activity className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats.active_monitors}</p>
                  <p className="text-xs text-blue-200">Active</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                  <Camera className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats.total_captures}</p>
                  <p className="text-xs text-blue-200">Captures</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                  <AlertTriangle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats.unacknowledged_alerts}</p>
                  <p className="text-xs text-blue-200">Alerts</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mt-6">
            <Button 
              onClick={() => setShowAddCamera(true)} 
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/30"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Camera
            </Button>
            <Button 
              onClick={() => setShowStartMonitor(true)} 
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
            >
              <Play className="w-4 h-4 mr-2" />
              Start Monitoring
            </Button>
            <Button 
              onClick={fetchData} 
              variant="outline"
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/30"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2 mt-6">
            {MONITORING_FEATURES.map((feature, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20"
              >
                <feature.icon className="w-4 h-4 text-cyan-300" />
                <span className="text-sm text-white/90">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Company Filter & Quick Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white rounded-2xl p-4 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-48">
            <Label className="text-xs text-gray-500 mb-1">Select Company</Label>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Feature Cards */}
        <div className="flex gap-2">
          {MONITORING_FEATURES.map((feature, idx) => (
            <div 
              key={idx}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl ${feature.bg} cursor-pointer hover:shadow-md transition-all`}
            >
              <feature.icon className={`w-5 h-5 ${feature.color}`} />
              <div>
                <p className="text-sm font-medium text-gray-900">{feature.label}</p>
                <p className="text-xs text-gray-500">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="rounded-2xl shadow-sm border-t-4 border-t-green-500 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Active Monitors</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.active_monitors}</p>
                  <p className="text-xs text-gray-400 mt-1">of {stats.total_monitors} total</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                  <Activity className="w-6 h-6 text-white" />
                </div>
              </div>
              {stats.total_monitors > 0 && (
                <Progress 
                  value={(stats.active_monitors / stats.total_monitors) * 100} 
                  className="h-1.5 mt-4" 
                />
              )}
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-sm border-t-4 border-t-blue-500 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Total Captures</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_captures}</p>
                  <p className="text-xs text-gray-400 mt-1">{stats.total_violations} violations detected</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <Camera className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-sm border-t-4 border-t-amber-500 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Unacknowledged</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.unacknowledged_alerts}</p>
                  <p className="text-xs text-gray-400 mt-1">alerts pending</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                  <Bell className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-sm border-t-4 border-t-purple-500 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Alert Rate</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.alert_rate}%</p>
                  <p className="text-xs text-gray-400 mt-1">{stats.critical_alerts} critical</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Alerts */}
      {alerts.length > 0 && (
        <Card className="rounded-2xl shadow-sm border-l-4 border-l-red-500 bg-gradient-to-r from-red-50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center text-red-900">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center mr-3 shadow-lg">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              Active Safety Alerts
              <Badge className="ml-3 bg-red-100 text-red-700">{alerts.length} pending</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.slice(0, 5).map((alert) => (
                <div key={alert.id} className="bg-white p-4 rounded-xl border border-red-100 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getSeverityColor(alert.severity)}>
                          {alert.severity.toUpperCase()}
                        </Badge>
                        <span className="text-sm text-gray-600">{alert.location}</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">{alert.violation_type}</h4>
                      <p className="text-sm text-gray-700 mb-2">{alert.description}</p>
                      <p className="text-xs text-gray-500">
                        Risk Score: {(alert.risk_score * 100).toFixed(0)}% • {new Date(alert.created_at).toLocaleString()}
                      </p>
                      {alert.recommended_actions && alert.recommended_actions.length > 0 && (
                        <div className="mt-2 text-sm text-gray-600">
                          <strong>Actions:</strong> {alert.recommended_actions.join(', ')}
                        </div>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => handleAcknowledgeAlert(alert.id)}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Acknowledge
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Monitors */}
      <Card className="rounded-2xl shadow-sm">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-2xl">
          <CardTitle className="flex items-center">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mr-3 shadow-lg">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <span>Active Monitors</span>
              <Badge className="ml-3 bg-blue-100 text-blue-700">
                {monitors.length === 0 ? 'No active' : `${monitors.length} running`}
              </Badge>
            </div>
          </CardTitle>
          <CardDescription>
            Real-time AI safety monitoring sessions
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {monitors.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Video className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Monitoring</h3>
              <p className="text-gray-600 mb-4">Start a monitoring session to begin AI safety analysis</p>
              <Button onClick={() => setShowStartMonitor(true)} className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                <Play className="w-4 h-4 mr-2" />
                Start Monitoring
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {monitors.map((monitor) => {
                const camera = cameras.find(c => c.id === monitor.camera_id);
                const isActive = monitor.status === 'active';
                return (
                  <div 
                    key={monitor.id} 
                    className={`rounded-xl border-2 p-4 transition-all hover:shadow-lg ${
                      isActive ? 'border-green-200 bg-gradient-to-br from-green-50 to-emerald-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow ${
                          isActive ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gray-400'
                        }`}>
                          <Camera className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">{camera?.camera_name || 'Unknown Camera'}</h4>
                          <p className="text-sm text-gray-500">{camera?.location}</p>
                        </div>
                      </div>
                      <Badge className={`${getStatusColor(monitor.status)} font-medium`}>
                        {isActive && <span className="w-2 h-2 bg-green-500 rounded-full inline-block mr-1 animate-pulse"></span>}
                        {monitor.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                        <p className="text-2xl font-bold text-gray-900">{monitor.total_captures}</p>
                        <p className="text-xs text-gray-500">Captures</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                        <p className="text-2xl font-bold text-red-600">{monitor.violations_detected}</p>
                        <p className="text-xs text-gray-500">Violations</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                        <p className="text-2xl font-bold text-gray-900">{monitor.capture_interval_seconds}s</p>
                        <p className="text-xs text-gray-500">Interval</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                        <p className="text-2xl font-bold text-gray-900">{(monitor.alert_threshold * 100).toFixed(0)}%</p>
                        <p className="text-xs text-gray-500">Threshold</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {monitor.status === 'active' && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleManualCapture(monitor.id)}
                            className="flex-1"
                          >
                            <Camera className="w-4 h-4 mr-1" />
                            Capture Now
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleUpdateMonitorStatus(monitor.id, 'paused')}
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {monitor.status === 'paused' && (
                        <Button 
                          size="sm" 
                          className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600"
                          onClick={() => handleUpdateMonitorStatus(monitor.id, 'active')}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Resume
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={() => handleUpdateMonitorStatus(monitor.id, 'stopped')}
                      >
                        <StopCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Cameras */}
      <Card className="rounded-2xl shadow-sm">
        <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50 rounded-t-2xl">
          <CardTitle className="flex items-center">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mr-3 shadow-lg">
              <Video className="w-5 h-5 text-white" />
            </div>
            <div>
              <span>Available Cameras</span>
              <Badge className="ml-3 bg-purple-100 text-purple-700">
                {cameras.length === 0 ? 'None configured' : `${cameras.length} cameras`}
              </Badge>
            </div>
          </CardTitle>
          <CardDescription>
            Configure CCTV and drone cameras for AI monitoring
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {cameras.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Camera className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Cameras Configured</h3>
              <p className="text-gray-600 mb-4">Add your first camera to start AI-powered safety monitoring</p>
              <Button onClick={() => setShowAddCamera(true)} className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add First Camera
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {cameras.map((camera) => (
                <Card key={camera.id}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{camera.camera_name}</h4>
                      <Badge className={camera.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {camera.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{camera.location}</p>
                    {monitors.find(m => m.camera_id === camera.id && m.status === 'active') ? (
                      <Badge className="bg-blue-100 text-blue-800">Monitoring Active</Badge>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setMonitorForm({...monitorForm, camera_id: camera.id});
                          setShowStartMonitor(true);
                        }}
                      >
                        Start Monitoring
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Camera Dialog */}
      <Dialog open={showAddCamera} onOpenChange={setShowAddCamera}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Camera/Drone Feed</DialogTitle>
            <DialogDescription>Configure a new camera or drone feed for monitoring</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCamera} className="space-y-4">
            <div>
              <Label htmlFor="camera_name">Camera Name *</Label>
              <Input
                id="camera_name"
                value={cameraForm.camera_name}
                onChange={(e) => setCameraForm({...cameraForm, camera_name: e.target.value})}
                placeholder="Main Entrance Camera"
                required
              />
            </div>
            <div>
              <Label htmlFor="location">Location *</Label>
              <Input
                id="location"
                value={cameraForm.location}
                onChange={(e) => setCameraForm({...cameraForm, location: e.target.value})}
                placeholder="Building A - Main Entrance"
                required
              />
            </div>
            <div>
              <Label htmlFor="stream_url">Stream URL</Label>
              <Input
                id="stream_url"
                value={cameraForm.stream_url}
                onChange={(e) => setCameraForm({...cameraForm, stream_url: e.target.value})}
                placeholder="rtsp://192.168.1.100/stream"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1">Add Camera</Button>
              <Button type="button" variant="outline" onClick={() => setShowAddCamera(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Start Monitoring Dialog */}
      <Dialog open={showStartMonitor} onOpenChange={setShowStartMonitor}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Live Monitoring</DialogTitle>
            <DialogDescription>Configure real-time safety analysis for a camera feed</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStartMonitoring} className="space-y-4">
            <div>
              <Label htmlFor="camera_id">Camera *</Label>
              <Select 
                value={monitorForm.camera_id} 
                onValueChange={(value) => setMonitorForm({...monitorForm, camera_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a camera" />
                </SelectTrigger>
                <SelectContent>
                  {cameras.map((camera) => (
                    <SelectItem key={camera.id} value={camera.id}>
                      {camera.camera_name} - {camera.location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="capture_interval">Capture Interval (seconds)</Label>
              <Input
                type="number"
                id="capture_interval"
                value={monitorForm.capture_interval_seconds}
                onChange={(e) => setMonitorForm({...monitorForm, capture_interval_seconds: parseInt(e.target.value)})}
                min="10"
                max="300"
              />
              <p className="text-xs text-gray-500 mt-1">How often to capture and analyze frames (10-300 seconds)</p>
            </div>
            <div>
              <Label htmlFor="alert_threshold">Alert Threshold</Label>
              <Input
                type="number"
                id="alert_threshold"
                value={monitorForm.alert_threshold}
                onChange={(e) => setMonitorForm({...monitorForm, alert_threshold: parseFloat(e.target.value)})}
                min="0"
                max="1"
                step="0.1"
              />
              <p className="text-xs text-gray-500 mt-1">Trigger alert when risk score exceeds this threshold (0.0-1.0)</p>
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1" disabled={!monitorForm.camera_id}>
                Start Monitoring
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowStartMonitor(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveMonitoringPage;
